import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Form, Loader } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useStore } from '../../../../../store/store';
import { useUpdateProspect } from '../../../../../api/services/lupp/prospect/prospect';
import type {
  Prospect,
  UpdateProspectRequest,
} from '../../../../../api/services/lupp/prospect/dto.prospect';
import {
  formatOffsetDateTime,
  buildProspectPayload,
  updateProspectContactTrackings,
  getNextEngagementDate,
} from '../utils';
import { localToUTC } from '../../../../../utils/tools';
import { createLeadFormFields, createTestDriveFormFields } from './formFields';
import styles from './DetailsTab.module.scss';

/** Delay before hiding success message after form submission (ms) */
const SUCCESS_MESSAGE_DELAY = 3000;
/** Delay to allow form initialization before setting values (ms) */
const FORM_INIT_DELAY = 300;

type ProspectDetailProps = {
  prospect?: Prospect;
  onDirtyChange?: (isDirty: boolean) => void;
  onCancel?: () => void;
  isTestDriveTab?: boolean;
  labels: Record<string, string>;
};

const DetailsTab: React.FC<ProspectDetailProps> = ({
  prospect,
  onDirtyChange,
  onCancel,
  isTestDriveTab = false,
  labels,
}) => {
  const { userIdentity } = useStore();
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const { mutate, isPending } = useUpdateProspect();
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const isInitialMount = useRef(true);
  const messageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FormRef>(null);

  // Use refs to keep stable references and prevent form reset
  const onCancelRef = useRef(onCancel);
  const onDirtyChangeRef = useRef(onDirtyChange);

  const organizationName = userIdentity?.organizationName;
  const partnerOrgLocation = userIdentity?.businessPartnerOrgLocation;

  // Update refs when props change
  useEffect(() => {
    onCancelRef.current = onCancel;
    onDirtyChangeRef.current = onDirtyChange;
  }, [onCancel, onDirtyChange]);

  // Reset initial mount flag when prospect or tab changes
  useEffect(() => {
    isInitialMount.current = true;
    // Notify parent that form is clean on mount/tab change
    onDirtyChangeRef.current?.(false);
    // Delay to allow form to fully initialize before accepting dirty state changes
    // The Form component's watch() triggers immediately, so we need to wait longer
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, FORM_INIT_DELAY);
    return () => clearTimeout(timer);
  }, [prospect?.prospectIdentifier, isTestDriveTab]);

  // Update nextEngagementDate field when prospect data changes
  useEffect(() => {
    if (formRef.current && prospect) {
      const nextEngagementDate = getNextEngagementDate(prospect);
      if (nextEngagementDate) {
        formRef.current.setValue('nextEngagementDate', nextEngagementDate);
      }
    }
  }, [prospect]);

  const handleFormDirtyChange = useCallback((isDirty: boolean) => {
    // Ignore dirty state changes during initial mount to prevent false positives
    if (isInitialMount.current) {
      return;
    }
    onDirtyChangeRef.current?.(isDirty);
  }, []);

  // Stable cancel handler that doesn't change reference
  const handleCancel = useCallback(() => {
    onCancelRef.current?.();
  }, []);

  // Get the first active test drive schedule for the form
  const activeTestDriveSchedule = useMemo(() => {
    const schedules = prospect?.testDriveSchedules || [];
    return schedules.find((schedule) => schedule.isActive && !schedule.isDeleted) || schedules[0];
  }, [prospect?.testDriveSchedules]);

  const formFields = useMemo(
    () =>
      isTestDriveTab
        ? createTestDriveFormFields(
            prospect,
            handleCancel,
            activeTestDriveSchedule,
            organizationName,
            labels,
            locale,
            partnerOrgLocation
          )
        : createLeadFormFields(prospect, handleCancel, organizationName, labels, locale),
    [
      prospect,
      handleCancel,
      isTestDriveTab,
      activeTestDriveSchedule,
      organizationName,
      labels,
      locale,
      partnerOrgLocation,
    ]
  );

  // Cleanup timeout on unmount to prevent memory leaks
  // Clear success message after delay (slider stays open)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        setSuccessMessage('');
      }, SUCCESS_MESSAGE_DELAY);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [successMessage]);

  // Scroll to message when error or success message appears
  useEffect(() => {
    if ((errorMessage || successMessage) && messageRef.current) {
      // Find the scrollable parent (slider body with overflow-y: auto)
      let parent = messageRef.current.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          parent.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        }
        parent = parent.parentElement;
      }
    }
  }, [errorMessage, successMessage]);

  const handleSuccess = (data: unknown) => {
    if (!prospect?.prospectIdentifier) {
      setErrorMessage(
        labels?.['details.prospectIdMissing'] ?? 'Prospect ID is missing. Cannot update.'
      );
      return;
    }

    // Prevent updates when status is Closed
    if (prospect?.statusName?.toLowerCase() === 'closed') {
      setErrorMessage(
        labels?.['details.closedProspectError'] ??
          'Cannot update a closed prospect. Please reopen the prospect to make changes.'
      );
      setSuccessMessage('');
      return;
    }

    setErrorMessage(null);

    // Get current timestamp for all date fields
    const currentDate = new Date();
    const now = formatOffsetDateTime(currentDate);

    let payload: UpdateProspectRequest;

    if (isTestDriveTab) {
      // Handle Test Drive form data
      const testDriveFormData = data as {
        testDriveNow?: string[];
        scheduledDate?: string;
        scheduledTime?: string;
        status?: string;
        selectedModelTrim?: string;
      };

      // Parse time slot to get start and end times
      const timeSlot = testDriveFormData.scheduledTime || '';
      const [startTime, endTime] = timeSlot.split('-').map((t) => t?.trim() || '');

      // Build scheduled datetime from date and time
      const scheduledDate = testDriveFormData.scheduledDate || '';
      const buildDatetime = (date: string, time: string): string => {
        if (!date || !time) return now;
        try {
          // Convert date to YYYY-MM-DD format if needed
          let isoDate = date;
          if (date.includes('/')) {
            // Date is in MM/DD/YYYY format, convert to YYYY-MM-DD
            const parts = date.split('/');
            if (parts.length === 3) {
              const [month, day, year] = parts;
              isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
          }

          const [hours, minutes] = time.split(':').map(Number);
          // Convert 12-hour format to 24-hour format (assuming AM times are < 6)
          const hour24 = hours < 6 ? hours + 12 : hours;
          // Use localToUTC to convert studio local time to UTC based on locale
          return localToUTC(isoDate, hour24, minutes, locale);
        } catch {
          return now;
        }
      };

      // Determine if test drive is selected
      const isTestDrive = testDriveFormData.testDriveNow?.includes('yes') ?? false;

      // Update or create test drive schedule
      const existingSchedule = activeTestDriveSchedule;
      const updatedTestDriveSchedule = {
        ...existingSchedule,
        testDriveStatus: testDriveFormData.status || existingSchedule?.testDriveStatus,
        scheduledStartDatetime: buildDatetime(scheduledDate, startTime),
        scheduledEndDatetime: buildDatetime(scheduledDate, endTime),
        appointmentLocationName:
          existingSchedule?.appointmentLocationName ||
          partnerOrgLocation ||
          prospect.prospectOwnerLocation ||
          '',
        trim: testDriveFormData.selectedModelTrim || existingSchedule?.trim,
        timezone: existingSchedule?.timezone || 'GMT',
        isActive: isTestDrive,
        isDeleted: false,
        createdDatetime: existingSchedule?.createdDatetime || now,
        updatedDatetime: now,
        updatedBy: 'portal-user',
      };

      payload = buildProspectPayload(prospect, now, {
        testDriveSchedules: [updatedTestDriveSchedule],
        isTestDrive: isTestDrive,
      });
    } else {
      const { isEmailOptIn, callConsent, ...restFormData } = data as Record<string, unknown>;

      payload = buildProspectPayload(prospect, now, {
        ...restFormData,
        isEmailOptIn: (isEmailOptIn as string[])?.includes('emailOptIn') || null,
        isCallOptIn: callConsent === 'callOptIn' || null,
        isCallOptOut: callConsent === 'callOptOut' || null,
        interestedTrim: Array.isArray(restFormData.interestedTrim)
          ? (restFormData.interestedTrim as string[]).join(',')
          : (restFormData.interestedTrim as string) || prospect.interestedTrim,
        prospectContactTrackings: updateProspectContactTrackings(
          prospect,
          restFormData.nextEngagementDate as string | undefined,
          now
        ),
        isTestDrive: activeTestDriveSchedule?.isActive ?? false,
      } as Partial<Prospect>);
    }

    // Remove email from payload to prevent updates (applies to both test drive and details)
    const { prospectEmail: _prospectEmail, ...payloadWithoutEmail } = payload;
    payload = payloadWithoutEmail as Prospect;

    mutate(
      { id: prospect.prospectIdentifier, data: payload },
      {
        onSuccess: () => {
          // Reset dirty state to prevent unsaved changes modal
          onDirtyChangeRef.current?.(false);
          setSuccessMessage(labels?.['details.updateSuccess'] ?? 'Prospect updated successfully!');
        },
        onError: (error: unknown) => {
          setSuccessMessage('');
          const errorMsg =
            error instanceof Error
              ? error.message
              : (labels?.['details.error'] ?? 'Failed to update prospect. Please try again.');
          setErrorMessage(errorMsg);
        },
      }
    );
  };

  const handleError = (error: unknown) => {
    console.error('Form validation failed:', error);
    setErrorMessage(
      labels?.['details.validationError'] ?? 'Please fix the form errors before submitting.'
    );
    setSuccessMessage('');
  };

  return (
    <Box ref={messageRef}>
      {successMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.successMessageText}>
          <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
            {successMessage}
          </Typography>
        </Box>
      )}
      {errorMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
          <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
            {errorMessage}
          </Typography>
        </Box>
      )}
      {isPending && (
        <Box className="fetchingOverlay">
          <Loader size={32} theme={invertedTheme} />
        </Box>
      )}
      <Form
        ref={formRef}
        id="create-prospect-form"
        theme={invertedTheme}
        formFields={formFields}
        onSuccess={handleSuccess}
        onError={handleError}
        onDirtyChange={handleFormDirtyChange}
        disableSubmitUntilDirty
        isFluid={true}
        locale={locale}
        language={language ?? locale.split('-')[0]}
      />
    </Box>
  );
};

export default DetailsTab;
